import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface ApplicationFormData {
  app_lot: string;
  app_type: string;
  app_date: string;
  app_desc: string;
  app_permit: string;
  app_doc?: string;
}

export default function AddApplication() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<ApplicationFormData>({
    app_lot: '',
    app_type: '',
    app_date: new Date().toISOString().split('T')[0],
    app_desc: '',
    app_permit: '',
    app_doc: ''
  });

  // Fetch available lots
  const { data: lots } = useQuery({
    queryKey: ['lots'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('land_lots')
        .select('lot_id, lot_community')
        .order('lot_id');
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch application types
  const { data: applicationTypes } = useQuery({
    queryKey: ['application-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('application_types')
        .select('app_type_id, app_desc')
        .order('app_desc');
      
      if (error) throw error;
      return data;
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const { error: submitError } = await supabase
        .from('lease_applications')
        .insert([{
          ...formData,
          app_final: 'N'
        }]);

      if (submitError) throw submitError;

      navigate('/admin/applications');
    } catch (err) {
      console.error('Error adding application:', err);
      setError(err instanceof Error ? err.message : 'Failed to add application');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/applications')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Add New Application</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create a new lease application record
          </p>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Lot *
              </label>
              <select
                value={formData.app_lot}
                onChange={(e) => setFormData(prev => ({ ...prev, app_lot: e.target.value }))}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                required
              >
                <option value="">Select a lot</option>
                {lots?.map((lot) => (
                  <option key={lot.lot_id} value={lot.lot_id}>
                    {lot.lot_id} - {lot.lot_community}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Application Type *
              </label>
              <select
                value={formData.app_type}
                onChange={(e) => setFormData(prev => ({ ...prev, app_type: e.target.value }))}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                required
              >
                <option value="">Select application type</option>
                {applicationTypes?.map((type) => (
                  <option key={type.app_type_id} value={type.app_type_id}>
                    {type.app_desc}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Application Date *
              </label>
              <input
                type="date"
                value={formData.app_date}
                onChange={(e) => setFormData(prev => ({ ...prev, app_date: e.target.value }))}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                value={formData.app_desc}
                onChange={(e) => setFormData(prev => ({ ...prev, app_desc: e.target.value }))}
                rows={3}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                placeholder="Enter application description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Permitted Use
              </label>
              <input
                type="text"
                value={formData.app_permit}
                onChange={(e) => setFormData(prev => ({ ...prev, app_permit: e.target.value }))}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                placeholder="Enter permitted use"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Application Document
              </label>
              <input
                type="text"
                value={formData.app_doc}
                onChange={(e) => setFormData(prev => ({ ...prev, app_doc: e.target.value }))}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                placeholder="Enter document reference or URL"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate('/admin/applications')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark disabled:opacity-50"
            >
              {isSubmitting ? 'Adding...' : 'Add Application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}